import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

export default function AlloggiatiCard() {
  const { lang } = useLang();
  const IT = lang === "it";
  const [status, setStatus] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState("prod");
  const [utente, setUtente] = useState("");
  const [password, setPassword] = useState("");
  const [wskey, setWskey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/alloggiati/status");
      setStatus(r.data);
      setEnabled(!!r.data.enabled);
      setMode(r.data.mode || "sandbox");
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { alloggiati_enabled: enabled, alloggiati_mode: mode };
      const creds = {};
      if (utente.trim()) creds.utente = utente.trim();
      if (password) creds.password = password;
      if (wskey) creds.wskey = wskey;
      if (Object.keys(creds).length > 0) payload.alloggiati_credentials = creds;
      await api.put("/properties/me", payload);
      toast.success(IT ? "Impostazioni Alloggiati salvate" : "Alloggiati settings saved");
      setPassword(""); setWskey("");
      await load();
    } catch (e) { toast.error(errMsg(e, IT ? "Errore salvataggio" : "Save failed")); }
    finally { setSaving(false); }
  };

  const testCreds = async () => {
    setTesting(true);
    try {
      const inline = (utente.trim() && password && wskey)
        ? { utente: utente.trim(), password, wskey, mode }
        : null;
      const r = await api.post("/alloggiati/test", inline || {});
      if (r.data.ok) toast.success(IT ? "Credenziali valide (token ricevuto)" : "Credentials OK (token received)");
      else toast.error(`${r.data.error || (IT ? "Autenticazione fallita" : "Auth failed")}${r.data.codice ? ` (${r.data.codice})` : ""}`);
    } catch (e) { toast.error(errMsg(e, IT ? "Test fallito" : "Test failed")); }
    finally { setTesting(false); }
  };

  const sendNow = async () => {
    if (!window.confirm(IT ? "Invia ora tutte le schedine di oggi ad Alloggiati Web?" : "Send all today's guest lines to Alloggiati Web now?")) return;
    setSending(true);
    try {
      const r = await api.post("/alloggiati/send-now", {});
      if (r.data.ok) toast.success(IT ? `Inviate ${r.data.sent} schedine` : `Sent ${r.data.sent} lines`);
      else toast.error(r.data.descrizione || r.data.error || (IT ? "Invio fallito" : "Send failed"));
      load();
    } catch (e) { toast.error(errMsg(e, IT ? "Invio fallito" : "Send failed")); }
    finally { setSending(false); }
  };

  const last = status?.last_run;
  return (
    <div className="border border-border rounded-lg p-6 bg-card" data-testid="alloggiati-card">
      <div className="flex items-center justify-between mb-4">
        <span className="uppercase tracking-[0.2em] text-[10px] text-muted-foreground">Alloggiati Web · Polizia di Stato</span>
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="alloggiati-enable-toggle"/>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        {IT ? "Se attivo, ogni notte alle 03:00 UTC un cron invia automaticamente le schedine del giorno precedente. Se disattivo, resta disponibile il download manuale del .txt." : "When on, a nightly cron at 03:00 UTC auto-submits yesterday's guest lines. When off, the manual .txt download still works."}
      </p>
      {mode === "sandbox" && (
        <div className="text-[11px] bg-sand/40 border border-border rounded px-2 py-1.5 mb-3" data-testid="alloggiati-sandbox-warning">
          ⚠️ {IT ? "L'endpoint sandbox è una VPN privata della Polizia (accessibile solo a integratori certificati) e non risponde dall'ambiente Emergent. Se hai credenziali reali, usa Produzione — è quello giusto per te." : "The sandbox endpoint is a private Police VPN not reachable from the cloud. If you have real credentials, use Production."}
        </div>
      )}
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setMode("sandbox")} className={`text-xs py-2 border rounded ${mode === "sandbox" ? "bg-primary text-white border-primary" : "border-border"}`} data-testid="alloggiati-mode-sandbox">Sandbox</button>
          <button type="button" onClick={() => setMode("prod")} className={`text-xs py-2 border rounded ${mode === "prod" ? "bg-primary text-white border-primary" : "border-border"}`} data-testid="alloggiati-mode-prod">Produzione</button>
        </div>
        <Input placeholder={status?.utente_masked ? `Utente (attuale: ${status.utente_masked})` : "Utente"} value={utente} onChange={(e) => setUtente(e.target.value)} data-testid="alloggiati-utente" className="text-sm"/>
        <Input placeholder={IT ? "Password (lascia vuoto per non modificare)" : "Password (blank keeps current)"} type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="alloggiati-password" className="text-sm"/>
        <Input placeholder={IT ? "WsKey (lascia vuoto per non modificare)" : "WsKey (blank keeps current)"} type="password" value={wskey} onChange={(e) => setWskey(e.target.value)} data-testid="alloggiati-wskey" className="text-sm"/>
        <div className="grid grid-cols-3 gap-2 pt-1">
          <Button size="sm" onClick={save} disabled={saving} data-testid="alloggiati-save">{saving ? "…" : (IT ? "Salva" : "Save")}</Button>
          <Button size="sm" variant="outline" onClick={testCreds} disabled={testing || (!status?.has_credentials && !(utente.trim() && password && wskey))} data-testid="alloggiati-test">{testing ? "…" : "Test"}</Button>
          <Button size="sm" variant="outline" onClick={sendNow} disabled={sending || !enabled || !status?.has_credentials} data-testid="alloggiati-send-now">{sending ? "…" : (IT ? "Invia oggi" : "Send today")}</Button>
        </div>
      </div>
      {last && (
        <div className="mt-4 text-[11px] border-t border-border pt-3" data-testid="alloggiati-last-run">
          <div className="flex items-center justify-between">
            <span className="uppercase tracking-wider text-muted-foreground">{IT ? "Ultimo invio" : "Last run"}</span>
            <span className={last.ok ? "text-sage" : "text-primary"}>{last.ok ? "OK" : "KO"}</span>
          </div>
          <div className="text-muted-foreground mt-1">{last.date} · {last.mode} · {IT ? "inviate" : "sent"} {last.sent}/{last.guests}{last.descrizione ? ` · ${last.descrizione}` : ""}</div>
        </div>
      )}
    </div>
  );
}
